/**
 * Shared input validation.
 *
 * Email validation lived in two places with two different patterns — Signup
 * had a function-local `emailRegex` and the change-email modal introduced
 * another — which meant an address could be accepted in one flow and rejected
 * in the other. Both now call this.
 *
 * Mirrors isValidEmail in core_backend restapi restAuthHelpers.ts. Keep the
 * two in sync: the frontend check is UX (an inline message instead of a failed
 * request), the backend one is the actual guard.
 */

/**
 * Requires a local part, an @, and a dotted domain with no empty labels, so
 * `a@b..com` and `a@b.` are rejected while `a.b+tag@sub.example.co.uk` is not.
 * Deliberately permissive beyond that — this is a typo guard, not a
 * deliverability check. Only a verification email can prove an address real,
 * and a stricter pattern would reject valid addresses for no gain.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/** Max length per RFC 5321. */
const EMAIL_MAX_LENGTH = 254;

export function isValidEmail(email: string): boolean {
  if (typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length === 0 || trimmed.length > EMAIL_MAX_LENGTH) return false;
  return EMAIL_PATTERN.test(trimmed);
}
